import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import MotionPhotosAutoIcon from "@mui/icons-material/MotionPhotosAuto";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { markSiteUnsynced } from "../../../utils/syncStore";
import {
  BulkMotionResult,
  MOTION_CHOICES,
  MOTION_CHOICE_DESCRIPTIONS,
  MOTION_CHOICE_LABELS,
  MotionChoice,
  bulkSetMotionOverride,
  choiceToOverride,
  inheritLabel,
} from "../../EditCamera/cameraMotionOverride";
import { fetchAllSiteCameras } from "./SiteAboutPageUtils/handleSetMilestoneMotion";
import { checkForUseMotion } from "./SiteAboutPageUtils/checkForUseMotion";
import { SiteCameras } from "./SiteAboutPageUtils/siteAboutInterfaces";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const BULK_MOTION_BUTTON_TEXT = "SET MOTION…";
export const BULK_MOTION_SYNC_NOTE =
  "Sync the site to apply. If the site is armed, accept the restart when you sync — re-arming alone won't apply it.";

/**
 * Site About → cameras toolbar: set the per-camera Motion setting (inherit /
 * use motion / always stream, ENG-2242) on the checked cameras. There is no
 * bulk endpoint, so it PATCHes each camera's general_info, four at a time,
 * then marks the site unsynced once and reloads the camera list so the
 * Use Motion column shows the new resolved values.
 */
export const BulkMotionButton = ({
  selected,
  siteCameras,
  siteInfo,
  setSiteCameras,
  setSelected,
  setSyncNeeded,
}: {
  selected: readonly string[];
  siteCameras: SiteCameras;
  siteInfo: any;
  setSiteCameras: Dispatcher<SiteCameras>;
  setSelected: Dispatcher<readonly string[]>;
  setSyncNeeded: Dispatcher<boolean>;
}) => {
  const [open, setOpen] = React.useState(false);
  const [choice, setChoice] = React.useState<MotionChoice>("inherit");
  const [running, setRunning] = React.useState(false);
  const [progress, setProgress] = React.useState({ done: 0, total: 0 });
  const [result, setResult] = React.useState<BulkMotionResult | null>(null);
  const [refreshFailed, setRefreshFailed] = React.useState(false);

  const selectedCameras = React.useMemo(
    () =>
      selected.map((id) => {
        const match = siteCameras?.results?.find(
          (camera) => String(camera.id) === String(id)
        );
        return { id, camera_name: match?.camera_name ?? `Camera ${id}` };
      }),
    [selected, siteCameras]
  );

  // The site's own value; on Milestone XProtect "inherit" means each camera's
  // milestone_motion instead, which differs per camera, so no "currently".
  const siteInherited =
    siteInfo?.integration_type_name === "milestone"
      ? null
      : typeof siteInfo?.use_motion === "boolean"
      ? siteInfo.use_motion
      : null;

  const openDialog = () => {
    setChoice("inherit");
    setResult(null);
    setRefreshFailed(false);
    setProgress({ done: 0, total: 0 });
    setOpen(true);
  };

  const close = () => {
    if (running) return;
    setOpen(false);
    if (result && result.succeeded.length > 0) setSelected([]);
  };

  const apply = async () => {
    const cameras = selectedCameras;
    if (cameras.length === 0 || running) return;
    setRunning(true);
    setResult(null);
    setProgress({ done: 0, total: cameras.length });
    const outcome = await bulkSetMotionOverride(
      cameras,
      choiceToOverride(choice),
      { onProgress: (done, total) => setProgress({ done, total }) }
    );
    if (outcome.succeeded.length > 0) {
      markSiteUnsynced(String(siteInfo.id));
      setSyncNeeded(true);
      try {
        const refreshed = await fetchAllSiteCameras(siteInfo.id);
        localStorage.setItem("siteCameras", JSON.stringify(refreshed));
        setSiteCameras(refreshed);
        checkForUseMotion(refreshed?.results ?? []);
      } catch {
        setRefreshFailed(true);
      }
    }
    setResult(outcome);
    setRunning(false);
  };

  const optionLabel = (option: MotionChoice) =>
    option === "inherit"
      ? inheritLabel(siteInherited)
      : MOTION_CHOICE_LABELS[option];

  return (
    <>
      <OrangeTextButton
        disabled={selected.length === 0}
        onClick={openDialog}
        text={
          <>
            <MotionPhotosAutoIcon fontSize="small" />
            &nbsp;{BULK_MOTION_BUTTON_TEXT}
          </>
        }
      />
      <Dialog
        open={open}
        onClose={close}
        aria-labelledby="bulk-motion-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="bulk-motion-dialog-title"
          sx={{ fontFamily: "Mulish" }}
        >
          Set motion for {selectedCameras.length} camera
          {selectedCameras.length === 1 ? "" : "s"}
        </DialogTitle>
        <DialogContent>
          {!result && (
            <RadioGroup
              aria-label="Motion setting"
              name="bulk-motion-choice"
              value={choice}
              onChange={(event) => {
                // Capture eagerly (see CLAUDE.md's setState-updater gotcha).
                const next = event.target.value as MotionChoice;
                setChoice(next);
              }}
            >
              {MOTION_CHOICES.map((option) => (
                <div key={option} className="mt-1">
                  <FormControlLabel
                    value={option}
                    disabled={running}
                    control={<Radio size="small" color="warning" />}
                    label={
                      <span className="font-mulish text-[14px] font-semibold">
                        {optionLabel(option)}
                      </span>
                    }
                  />
                  <p className="ml-[30px] -mt-1 font-mulish text-[13px] text-[#5B6B86]">
                    {MOTION_CHOICE_DESCRIPTIONS[option]}
                  </p>
                </div>
              ))}
            </RadioGroup>
          )}
          {running && (
            <div className="mt-3 font-mulish text-[13px]" role="status">
              Updating {progress.done} of {progress.total}…
              <LinearProgress
                variant="determinate"
                color="warning"
                value={
                  progress.total ? (progress.done / progress.total) * 100 : 0
                }
                sx={{ mt: 1 }}
              />
            </div>
          )}
          {result && (
            <div
              className="font-mulish text-[14px] text-actuate-blue"
              data-testid="bulk-motion-summary"
            >
              <p className="font-bold">
                Updated {result.succeeded.length} of{" "}
                {result.succeeded.length + result.failed.length} cameras.
              </p>
              {result.failed.length > 0 && (
                <div role="alert" className="mt-2">
                  <p className="text-[#D93636]">
                    {result.failed.length} camera
                    {result.failed.length === 1 ? "" : "s"} failed:
                  </p>
                  <ul className="ml-5 mt-1 list-disc text-[13px]">
                    {result.failed.map(({ camera, error }) => (
                      <li key={camera.id}>
                        {camera.camera_name}: {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.succeeded.length > 0 && (
                <p className="mt-2 rounded border-l-4 border-actuate-orange bg-[#FFF4E5] px-3 py-2 text-[13px]">
                  {BULK_MOTION_SYNC_NOTE}
                </p>
              )}
              {refreshFailed && (
                <p className="mt-2 text-[13px] text-[#5B6B86]">
                  The camera list couldn&apos;t be refreshed; reload the page to
                  see the updated Use Motion column.
                </p>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {result ? (
            <OrangeTextButton disabled={false} onClick={close} text="Close" />
          ) : (
            <>
              <OrangeTextButton
                disabled={running}
                onClick={close}
                text="Cancel"
              />
              <OrangeTextButton
                disabled={running || selectedCameras.length === 0}
                onClick={apply}
                text={running ? "Applying…" : "Apply"}
              />
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
